import * as React from 'react';

import { enumAccountType } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  DataGridColumn,
  HeaderIcons,
  LocalizedPhone,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { AccountRecord } from '../../../_graphql/queryAccounts';
import * as accountListState from '../../../_state/accountList.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { AccountActionsMenu } from '../AccountActionsMenu';
import { AccountTypeFilter } from '../AccountTypeFilter';

type UseAccountListColumnsArgs = {
  onRowClick: (record: AccountRecord) => void;
  onDeleteAccount: (params: { id: string }) => void;
};

export const useAccountListColumns = ({
  onRowClick,
  onDeleteAccount,
}: UseAccountListColumnsArgs): DataGridColumn<AccountRecord>[] => {
  const t = useTranslations();
  const accountTypes = useAtomValue(accountListState.$accountTypes);

  return React.useMemo(
    () => [
      {
        key: 'name',
        header: t.accountsView.table.columns.name,
        icon: HeaderIcons.Text,
        grow: 1,
        resizable: true,
        cell: ({ row: record }) => record.name,
        onDoubleClick: ({ row: record }) => onRowClick(record),
      },
      {
        key: 'type',
        header: t.accountsView.table.columns.type,
        icon: HeaderIcons.Tag,
        resizable: true,
        onDoubleClick: ({ row: record }) => onRowClick(record),
        columnFilter: ({ close }) => <AccountTypeFilter onClose={close} />,
        isFilterActive: accountTypes.size > 0,
        cell: ({ row: record }) => (
          <div className="flex flex-wrap gap-1">
            {record.type === enumAccountType.Commercial && (
              <Badge size="small" color="rust">
                {t.accountsView.form.type.commercial}
              </Badge>
            )}
            {record.type === enumAccountType.Individual && (
              <Badge size="small" color="olive">
                {t.accountsView.form.type.individual}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'email',
        header: t.accountsView.table.columns.email,
        icon: HeaderIcons.Email,
        resizable: true,
        onDoubleClick: ({ row: record }) => onRowClick(record),
        cell: ({ row: record }) => record.email || '',
      },
      {
        key: 'phone',
        header: t.accountsView.table.columns.phone,
        icon: HeaderIcons.Phone,
        resizable: true,
        width: 200,
        onDoubleClick: ({ row: record }) => onRowClick(record),
        cell: ({ row: record }) => <LocalizedPhone children={record.phone} />,
      },
      {
        key: 'relationship',
        header: t.accountsView.table.columns.relationship,
        icon: HeaderIcons.Tags,
        width: 180,
        resizable: true,
        onDoubleClick: ({ row: record }) => onRowClick(record),
        cell: ({ row: record }) => (
          <div className="flex flex-wrap gap-1">
            {record.relationship.isCustomer && (
              <Badge size="small" color="teal">
                {t.accountsView.relationship.customer}
              </Badge>
            )}
            {record.relationship.isSupplier && (
              <Badge size="small" color="slate">
                {t.accountsView.relationship.supplier}
              </Badge>
            )}
            {record.relationship.isCarrier && (
              <Badge size="small" color="teal">
                {t.accountsView.relationship.carrier}
              </Badge>
            )}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row: record }) => (
          <AccountActionsMenu
            account={record}
            onDelete={(id) => onDeleteAccount({ id })}
          />
        ),
      },
    ],
    [t, onRowClick, onDeleteAccount, accountTypes],
  );
};
